import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Alert, AlertTitle, Button } from '@mui/material';
import { useClient } from '../../../context/ClientContext';
import ClientQuestionBase from '../common/ClientQuestionBase';
import { SimpleLoader } from '../../../components/loading/simpleLoader';
import FaceScanInstruction from '../../../components/faceScanInstruction/faceScanInstruction';
import ClientButton from '../common/Clientbutton';

export default function MasterPreFaceScan() {
  const { clientId, theme, landing } = useClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { isFaceScanFailed } = location.state || {};
  const [ currentStep, setCurrentStep ] = useState( 0 );
  const [ isLoading, setIsLoading ] = useState( false );
  const [ error, setError ] = useState( null );
  const { encodedKey } = landing;
  // Check for redirect URL
  useEffect( () => {
    if ( !isFaceScanFailed ) {
      try {
        const redirectUrl = localStorage.getItem( `${ clientId }-redirect-url` );
        if ( !redirectUrl ) {
          setError( 'Missing redirect URL. Please complete the demographics form first.' );
          navigate( `/${ clientId }-demographics?key=${ encodedKey }` );
        }
      } catch ( err ) {
        setError( 'Failed to access local storage. Please try again.' );
      }
    }
  }, [ navigate, isFaceScanFailed, clientId ] );

  const redirectToFaceScan = async () => {
    try {
      setIsLoading( true );
      const redirectUrl = localStorage.getItem( `${ clientId }-redirect-url` );
      if ( redirectUrl ) {
        window.localStorage.setItem( `from_${ clientId }`, true );
        window.location.href = redirectUrl;
      } else {
        throw new Error( 'Redirect URL not found' );
      }
    } catch ( err ) {
      setError( 'Failed to proceed to face scan. Please try again.' );
      setIsLoading( false );
    }
  };

  const handleNextStep = () => {
    if ( currentStep === 0 ) {
      setCurrentStep( 1 );
    } else {
      redirectToFaceScan();
    }
  };

  const handleBackStep = () => {
    if ( currentStep === 0 ) {
      navigate( `/${ clientId }-demographics?key=${ encodedKey }`, { state: { visitAsBack: true } } );
    } else {
      setCurrentStep( 0 );
    }
  };

  const handleDismissError = () => {
    setError( null );
  };

  const handleHomeClick = () => {
    navigate( `/${ clientId }-landing?key=${ encodedKey }` );
  };

  if ( isLoading ) {
    return (
      <SimpleLoader
        loadingText="Loading..."
        textColor={ theme.primaryColor }
      />
    );
  }

  // Render content based on current step
  const renderContent = () => {
    switch ( currentStep ) {
      case 0:
        return (
          <Box sx={ {
            width: '100%',
            maxWidth: '600px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: "center"
          } }>
            <Typography sx={ {
              fontSize: { xs: '20px', sm: '25px', md: '30px' },
              fontWeight: 700,
              lineHeight: 1.2,
              textAlign: 'center',
              color: '#30373E',
              mb: 2
            } }>
              Now, we will scan your face to measure your HRV.
            </Typography>
            <Typography sx={ {
              fontSize: { xs: '13px', sm: '15px', md: '15px' },
              fontWeight: 400,
              lineHeight: { xs: '18px', sm: '22px', md: "24px" },
              textAlign: 'center',
              mb: 2
            } }>
              Heart rate variability (HRV) is related to stress, aging and other metrics of physical and mental health.
            </Typography>
            <Typography sx={ {
              fontSize: { xs: '13px', sm: '15px', md: '15px' },
              fontWeight: 400,
              lineHeight: { xs: '18px', sm: '22px', md: "24px" },
              textAlign: 'center',
              mb: 2
            } }>
              Based on these results, we can curate product recommendations that will work specifically for you.
            </Typography>
            <Typography sx={ {
              fontSize: { xs: '13px', sm: '15px', md: '15px' },
              fontWeight: 400,
              lineHeight: { xs: '18px', sm: '22px', md: "24px" },
              textAlign: 'center',
              mb: 4
            } }>
              💡 Please make sure you are in a brightly lit area and the light is spread evenly on your face. Remove your hat and eyeglasses and stay close to the camera and still during the 30-second scan to get a good measurement.
            </Typography>
            <ClientButton
              text="Next"
              onClick={ handleNextStep }
              backgroundColor={ theme.primaryColor }
              sx={ {
                maxWidth: "342px",
                width: "100%",
              } }
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={ {
            width: '100%',
            maxWidth: '600px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          } }>
            <FaceScanInstruction
              videoContainerSx={ {
                marginY: { xs: "12px", sm: "16px", md: "20px" }
              } }
            />
            <ClientButton
              text="Let's go!"
              onClick={ handleNextStep }
              backgroundColor={ theme.primaryColor }
              sx={ {
                maxWidth: "342px",
                width: "100%",
                mt: 4
              } }
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Error Alert */ }
      { error && (
        <Box sx={ {
          position: 'fixed',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '600px',
          zIndex: 1000
        } }>
          <Alert
            severity="error"
            variant="filled"
            onClose={ handleDismissError }
            sx={ { mb: 2 } }
            action={
              <Button
                color="inherit"
                size="small"
                onClick={ handleHomeClick }
                sx={ {
                  textDecoration: 'underline',
                  '&:hover': {
                    textDecoration: 'none'
                  }
                } }
              >
                Return Home
              </Button>
            }
          >
            <AlertTitle>Error</AlertTitle>
            { error }
          </Alert>
        </Box>
      ) }

      <ClientQuestionBase
        IsShowBackIcon={ currentStep === 0 ? false : true }
        backButtonHandler={ handleBackStep }
        aimdLinearBarValue={ 20 }
        isShowStepper={ true }
      >
        { renderContent() }
      </ClientQuestionBase>
    </>
  );
}